import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    vehicle: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5ntdeg0pjq8qvj95bag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          vehicle: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 sm:mb-16 text-center">
          <div className="mb-6 sm:mb-8 rounded-2xl sm:rounded-3xl overflow-hidden mx-auto max-w-5xl h-48 sm:h-64 lg:h-80">
            <img
              src="https://public.readdy.ai/ai/img_res/af9b2d05f711c1b4d8e6a92dcd34339f.jpg"
              alt="Premium Detailing"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
            Ready to restore your vehicle's <em className="font-light text-gray-600 not-italic">brilliance</em>?<br className="hidden sm:block" />
            Book your detailing appointment today and experience{' '}
            <strong className="font-bold">professional care</strong> that lasts.
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          <div className="order-2 lg:order-1">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">Get In Touch</h3>
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#00D9FF] rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-base sm:text-lg text-gray-900 mb-1">Visit Us</div>
                  <div className="text-gray-700 text-sm">
                    246 N Mathewson<br />
                    Wichita, KS<br />
                    United States
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#00D9FF] rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-base sm:text-lg text-gray-900 mb-1">Call Us</div>
                  <a
                    href="tel:+13163007524"
                    className="text-gray-700 text-sm hover:text-[#00D9FF] transition-colors"
                  >
                    (316) 300-7524
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#00D9FF] rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-base sm:text-lg text-gray-900 mb-1">Email Us</div>
                  <a
                    href="mailto:darnelmd@icloud.com"
                    className="text-gray-700 text-sm hover:text-[#00D9FF] transition-colors break-all"
                  >
                    darnelmd@icloud.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#00D9FF] rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-base sm:text-lg text-gray-900 mb-1">Business Hours</div>
                  <div className="text-gray-700 text-sm">
                    Monday - Friday: 7:00 AM - 6:00 PM<br />
                    Saturday: 7:00 AM - 4:00 PM<br />
                    Sunday: Closed
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-900 mb-2">
                  Service Interested In *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Interior Deep Clean">Interior Deep Clean</option>
                  <option value="Exterior Detailing">Exterior Detailing</option>
                  <option value="Paint Correction">Paint Correction &amp; Enhancement</option>
                  <option value="Ceramic Coating">Ceramic Coating</option>
                  <option value="Full Reconditioning">Full Reconditioning</option>
                  <option value="Headlight Restoration">Headlight Restoration</option>
                  <option value="Stereo Installation">Stereo Installation</option>
                  <option value="Audio System Upgrade">Audio System Upgrade</option>
                </select>
              </div>

              <div>
                <label htmlFor="vehicle" className="block text-sm font-medium text-gray-900 mb-2">
                  Vehicle Make & Model
                </label>
                <input
                  type="text"
                  id="vehicle"
                  name="vehicle"
                  value={formData.vehicle}
                  onChange={handleChange}
                  placeholder="e.g., 2020 BMW 3 Series"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                  Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  placeholder="Tell us about your vehicle's condition or any specific concerns..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent outline-none transition-all resize-none text-sm"
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Schedule Now'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                  Thank you! We'll contact you shortly to confirm your appointment.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
